import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import moment from "moment";
import { z } from "zod";

import { UploadCertSchema } from "../../shared/api-types/certificate";
import type { UsersUploadCert } from "../../shared/api-types/certificate";
import type { UserCrew } from "../../shared/api-types/user";

import type { UploadableCourse } from "../../features/bulk-certificate-upload";

import { usePositionValidPeriods, NEVER_EXPIRES_SENTINEL } from "./usePositionValidPeriods";
import type { PeriodsResult } from "./usePositionValidPeriods";

export type SubmitPayload = UsersUploadCert & { isCustom: boolean; files: File[] };

export interface FormInput {
  course: UploadableCourse | null;
  selectedUsers: UserCrew[];
  files: File[];
  completed: string;
  validUntil: string | null;
  comment: string;
  neverExpires: boolean;
}

const createFormSchema = (
  crewId: number,
  /** When set, course and selectedUsers are fixed — skip their validation */
  fixedCourseId?: number,
  fixedUserIds?: number[],
) =>
  UploadCertSchema.pick({ completed: true, validUntil: true, comment: true })
    .extend({
      course: z.custom<UploadableCourse | null>((v) => (fixedCourseId != null ? true : v !== null && v !== undefined), {
        message: i18next.t("zod_errors.required"),
      }),
      selectedUsers: z.array(z.custom<UserCrew>()).min(fixedUserIds != null ? 0 : 1),
      files: z.array(z.instanceof(File)).min(1),
      neverExpires: z.boolean(),
    })
    .refine((v) => v.neverExpires || v.validUntil != null, {
      message: i18next.t("zod_errors.required"),
      path: ["validUntil"],
    })
    .transform(
      (v): SubmitPayload => ({
        crewId,
        courseId: fixedCourseId ?? v.course!.id,
        isCustom: fixedCourseId != null ? false : v.course!.isCustom,
        userIds: fixedUserIds ?? v.selectedUsers.map((u) => u.id),
        files: v.files,
        completed: v.completed,
        validUntil: v.neverExpires ? null : v.validUntil,
        comment: v.comment,
      }),
    );

// ============= Hook =============

interface UseCertificateFormOptions {
  crewId: number;
  /** Fixed when used for single upload; undefined when user picks in step 1 */
  fixedCourse?: { id: number; isCustom: boolean; validityPeriodMonths: number | null };
  /** Fixed when used for single upload; undefined when user picks in step 2 */
  fixedUserIds?: number[];
}

export function useCertificateForm({ crewId, fixedCourse, fixedUserIds }: UseCertificateFormOptions) {
  const form = useForm<FormInput, unknown, SubmitPayload>({
    resolver: zodResolver(createFormSchema(crewId, fixedCourse?.id, fixedUserIds)) as any,
    mode: "onChange",
    defaultValues: {
      course: null,
      selectedUsers: [],
      files: [],
      completed: "",
      validUntil: null,
      comment: "",
      neverExpires: false,
    },
  });

  const { watch, setValue, getValues } = form;
  const completedDate = watch("completed") ?? "";
  const neverExpires = watch("neverExpires") ?? false;
  const selectedCourse = watch("course");
  const selectedUsers = watch("selectedUsers") ?? [];

  // Resolve which course/user ids feed the validity period hook
  const courseId = fixedCourse?.id ?? selectedCourse?.id ?? 0;
  const isCustom = fixedCourse?.isCustom ?? selectedCourse?.isCustom ?? false;
  const userIds = fixedUserIds ?? selectedUsers.map((u) => u.id);
  const courseDefaultMonths = fixedCourse?.validityPeriodMonths ?? selectedCourse?.validityPeriodMonths ?? null;

  const validityHook = usePositionValidPeriods(crewId, courseId, isCustom, userIds, courseDefaultMonths);

  const { resolvedMonths, divergentPeriods, allFromPositionOverride, allNeverExpires } = validityHook.getResult();
  const courseIsNeverExpires = courseDefaultMonths === 0;
  const isDivergent = divergentPeriods !== null;
  const effectiveNeverExpires = allNeverExpires || (courseIsNeverExpires && !allFromPositionOverride && !isDivergent);

  // The last validUntil derived from completedDate, so it can be withdrawn without touching a date the
  // user typed into Valid Until themselves.
  const autoValidUntil = useRef<string | null>(null);

  // Auto-calc validUntil when completedDate changes
  useEffect(() => {
    if (neverExpires) return;
    if (!completedDate) {
      // The completion date was cleared or rejected: a Valid Until derived from it is stale.
      if (autoValidUntil.current !== null && getValues("validUntil") === autoValidUntil.current) {
        setValue("validUntil", null, { shouldValidate: true });
      }
      autoValidUntil.current = null;
      return;
    }
    const { resolvedMonths } = validityHook.getResult();
    if (!resolvedMonths) return;
    const m = moment(completedDate, "YYYY-MM-DD");
    if (m.isValid()) {
      const validUntil = m.clone().add(resolvedMonths, "months").endOf("month").format("YYYY-MM-DD");
      autoValidUntil.current = validUntil;
      setValue("validUntil", validUntil, { shouldValidate: true });
    }
  }, [completedDate]);

  // Clear validUntil when neverExpires is checked
  useEffect(() => {
    if (neverExpires) {
      setValue("validUntil", null);
    }
  }, [neverExpires]);

  // Auto-check/uncheck neverExpires based on resolved periods
  useEffect(() => {
    if (effectiveNeverExpires) {
      setValue("neverExpires" as any, true, { shouldValidate: true });
    } else if (resolvedMonths !== null || isDivergent) {
      setValue("neverExpires" as any, false, { shouldValidate: true });
    }
  }, [effectiveNeverExpires, resolvedMonths, isDivergent]);

  const getTooltipContent = (t: (key: string, opts?: object) => string): string => {
    if (divergentPeriods) {
      const parts = Array.from(divergentPeriods.entries())
        .sort(([a], [b]) => a - b)
        .map(([months, count]) =>
          months === NEVER_EXPIRES_SENTINEL
            ? t("certificate_upload.validity_period_never_expires_count", { count })
            : t("certificate_upload.validity_period_user_count", { months, count }),
        );
      return t("certificate_upload.validity_period_divergent", { breakdown: parts.join(", ") });
    }
    if (effectiveNeverExpires) return t("validity_period_never_expires");
    if (!resolvedMonths) return t("validity_period");
    if (allFromPositionOverride && resolvedMonths !== courseDefaultMonths) {
      return t("validity_period_position_override", {
        positionName: t("certificate_upload.bulk.validity_period_position_override"),
        positionMonths: resolvedMonths,
        courseMonths: courseDefaultMonths ?? 0,
      });
    }
    return t("validity_period_course_default", { months: courseDefaultMonths ?? resolvedMonths });
  };

  return {
    form,
    validityHook,
    neverExpires,
    periodsResult: { resolvedMonths, divergentPeriods, allFromPositionOverride, allNeverExpires } as PeriodsResult,
    effectiveNeverExpires,
    getTooltipContent,
  };
}
